import React, { CSSProperties, useState } from 'react';
import cn from 'classnames';
import { Section } from '@views/Settings/components';
import { LoaderIcon, PlusIcon } from '@components/Icons';
import styles from './WorkspaceAvatar.module.scss';
import {
  acceptedAvatarImageTypes,
  useWorkspaceContext,
} from '@support/hooks/use-workspace-context';
import { apiRequest } from '@support/api';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { WorkspaceAPIItem } from '@models/Workspace';

export default function WorkspaceAvatar() {
  const { activeWorkspace, updateActiveWorkspace, userIsAdmin } = useWorkspaceContext();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(undefined);
  const [workspaceAvatarURL, setWorkspaceAvatarURL] = useState<string | null>(
    activeWorkspace?.avatarURL,
  );

  const uploadImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaving(true);
    setError(undefined);

    const upload = e.target as HTMLInputElement;
    const file = upload.files[0];

    if (!file) {
      setSaving(false);
      return;
    }

    if (file && !acceptedAvatarImageTypes.has(file?.type)) {
      setSaving(false);
      setError('Only PNG or JPG files can be uploaded');
      return;
    }

    const formData = new FormData();
    const url = URL.createObjectURL(file);
    const originalAvatarURL = activeWorkspace?.avatarURL;

    setWorkspaceAvatarURL(url);
    updateActiveWorkspace({ avatarURL: url });
    formData.append('avatar_url', file);
    apiRequest<WorkspaceAPIItem>('PATCH', '/workspaces/active/', formData, { apiVersion: 'v2' })
      .then((workspace) => {
        updateActiveWorkspace({ avatarURL: workspace.avatar_url });
      })
      .catch((err) => {
        console.error(err);
        updateActiveWorkspace({ avatarURL: originalAvatarURL });
        setError('Error uploading image, please try again');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const status = saving ? 'saving' : error ? 'error' : 'default';

  return userIsAdmin ? (
    <Section>
      <label className={styles.container}>
        {workspaceAvatarURL ? (
          <>
            <div
              className={cn(styles.box, styles.imagePreview)}
              style={
                {
                  '--bg-image': `url(${workspaceAvatarURL})`,
                } as CSSProperties
              }
            />
            <StateBasedFadeTransition
              state={status}
              elementByStateMap={{
                default: <span>Upload new workspace image</span>,
                saving: (
                  <span className={styles.saving}>
                    <LoaderIcon width={11} height={11} /> Saving
                  </span>
                ),
                error: <span>{error}</span>,
              }}
            />
          </>
        ) : (
          <>
            <div className={styles.box}>
              <PlusIcon width={24} height={24} />
            </div>
            Add workspace image
          </>
        )}
        <input
          className={styles.fileInput}
          type="file"
          accept=".png,.jpg,.jpeg"
          onChange={uploadImage}
        />
      </label>
    </Section>
  ) : (
    <Section>
      <div
        className={cn(styles.box, styles.imagePreview, styles.readOnly)}
        style={
          {
            '--bg-image': `url(${workspaceAvatarURL})`,
          } as CSSProperties
        }
      />
    </Section>
  );
}
